import * as React from 'react';

import PropTypes from 'prop-types';
import { InjectedRouter } from 'react-router';

// @ts-ignore
import OrganizationActions from 'actions/OrganizationActions';
import ToastActions from 'actions/ToastActions';
import { withRouter } from 'app/context/Router';
// @ts-ignore
import Organizations from 'collections/Organizations';
import BoxBody from 'components/BoxBody';
import BoxFooter from 'components/BoxFooter';
import Button from 'components/Button';
import DialogContent from 'components/DialogContent';
import Field from 'components/Field';
import Label from 'components/Label';
import LoadingIndicator from 'components/LoadingIndicator';
import SchoolPicker from 'components/SchoolPicker';
// @ts-ignore
import { NO_SCHOOL } from 'constants/OrganizationConstants';
import Group from 'entities/Group';
import Organization from 'entities/Organization';
// @ts-ignore
import GroupActions from 'groups/actions/GroupActions';
import { loadGQLUserOwnedGroups } from 'groups/lib/GroupPromises';
import EventTracking from 'lib/EventTracking';
// @ts-ignore
import UserActions from 'user/UserActions';

import './styles/SetupSchool.scss';

const EVENT_SCREEN_NAME = 'SetupSchool';

type Props = {
  router: InjectedRouter;
};

type State = {
  schoolId?: number;
  submitting: boolean;
  suggestedSchools?: Organizations | null;
};

class SetupSchool extends React.Component<Props, State> {
  state: State = {
    schoolId: undefined,
    submitting: false,
    suggestedSchools: null,
  };

  static childContextTypes = {
    eventScreenName: PropTypes.string,
  };

  getChildContext() {
    return {
      eventScreenName: EVENT_SCREEN_NAME,
    };
  }

  async componentDidMount() {
    EventTracking.view(EVENT_SCREEN_NAME);

    Organizations.suggest().then(
      organizations => {
        if (organizations && organizations.length) {
          this.setState({ suggestedSchools: organizations });
        }
      },
      (e: Error) => {
        console.debug(e);
        console.error('SetupSchool - can’t get organization suggestions');
      }
    );
  }

  handleSchoolCreate = (organization: Organization) => {
    // $FlowFixMe - no type coercion for 0.93.0
    this.setState({ schoolId: organization.id });
  };

  async getFirstOwnedClass(): Promise<Group | undefined> {
    const ownedGroups = await loadGQLUserOwnedGroups(true);
    if (ownedGroups && ownedGroups.length > 0) {
      return Group.fromJSON(ownedGroups[0]);
    }
  }

  handleDontWorkClick = () => {
    UserActions.savePreference('works_at_a_school', false)
      // $FlowFixMe - no type coercion for 0.93.0
      .then(() => this.updateUserOrganization(NO_SCHOOL.id))
      .catch(ToastActions.showDefaultErrorToast);
  };

  handleSaveSchoolButtonClick = () => {
    if (this.state.schoolId === NO_SCHOOL.id) {
      this.handleDontWorkClick();
    } else {
      this.updateUserOrganization(this.state.schoolId);
    }
  };

  redirectToApp = () => {
    this.props.router.push('/');
  };

  updateUserOrganization = async (organizationId?: number) => {
    const onFailure = (error: Error) => {
      this.setState({ submitting: false });
      ToastActions.showAPIError(error);
    };

    const onSuccess = () => {
      OrganizationActions.loadUserOrganizations();
      ToastActions.showSuccessToast('Saved!');
      this.redirectToApp();
    };

    const updateUserOrganization = () => {
      UserActions.update({ organization_id: organizationId }).then(onSuccess, onFailure);
    };

    const firstOwnedClass = await this.getFirstOwnedClass();

    const linkClassToOrg = (groupToPatch: Group) => {
      const attrs = { organization_id: organizationId };
      GroupActions.patch(groupToPatch, attrs).then(updateUserOrganization, onFailure);
    };

    this.setState({ submitting: true });

    if (firstOwnedClass) {
      // we make the assumption that the user's first owned class
      // was the one that they created during the setup flow
      // and link it to the selected organization
      linkClassToOrg(firstOwnedClass);
    } else {
      // if the user did not create a class, we simply link
      // the user to the organization
      updateUserOrganization();
    }
  };

  renderDontWorkInASchoolButton() {
    return (
      <BoxFooter className="SetupSchool-footer">
        <Button
          eventElementName="dontWorkButton"
          className="SetupSchool-dontWorkButton"
          level="text"
          size="small"
          onClick={this.handleDontWorkClick}>
          I don’t work at a school
        </Button>
      </BoxFooter>
    );
  }

  render() {
    return (
      <DialogContent
        className="SetupSchool"
        title="Add your school"
        subtext={'Connect with colleagues and help students and parents find your classes.'}
        size="small">
        <BoxBody divided>
          <Field className="SetupGroup-field">
            <Label>School</Label>
            <SchoolPicker
              schoolId={this.state.schoolId}
              suggestions={this.state.suggestedSchools || []}
              placeholder="Search for a school"
              onSelect={value => this.setState({ schoolId: Number(value) })}
              onClear={() => this.setState({ schoolId: undefined })}
              onCreateOrganization={this.handleSchoolCreate}
              onNoSchool={this.handleDontWorkClick}
              noQuery="Suggested schools for you"
            />
          </Field>
          {this.state.submitting ? (
            <LoadingIndicator className="SetupSchool-saveSchoolButton-loader" size="small" />
          ) : (
            <Button
              className="SetupSchool-saveSchoolButton"
              eventElementName="saveSchoolButton"
              disabled={!this.state.schoolId || this.state.submitting}
              onClick={this.handleSaveSchoolButtonClick}>
              {'Save'}
            </Button>
          )}
        </BoxBody>
        {this.renderDontWorkInASchoolButton()}
      </DialogContent>
    );
  }
}

export { SetupSchool };
export default withRouter(SetupSchool);
